import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, Crown, ArrowRight, Glasses, Monitor, Smartphone, Zap, ChevronDown } from 'lucide-react';
import { supabase } from '../config/supabase';
import WorldsGallery from '../components/WorldsGallery';
import Showcase from '../components/landing/Showcase';
import HeroCrew from '../components/landing/HeroCrew';
import Ticker from '../components/landing/Ticker';
import ClassBanner from '../components/classes/ClassBanner';
import { COPY, readLook } from '../components/landing/looks';
import '../components/landing/Looks.css';
import './LandingPage.css';
import '../components/landing/Hero.css';

const LandingPage: React.FC = () => {
  const [worldName, setWorldName] = useState('');
  const nameInput = useRef<HTMLInputElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [look] = useState(readLook);
  const c = COPY[look];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const createWorld = () => {
    if (!worldName.trim()) return;
    const worldId = Date.now().toString();
    const dest = `/world/${worldId}?name=${encodeURIComponent(worldName)}`;
    // Building/creating requires a (free) account. Guests sign up first and
    // land straight in their new world afterwards.
    if (!user) {
      navigate(`/login?mode=signup&next=${encodeURIComponent(dest)}`);
      return;
    }
    navigate(dest);
  };

  // Closing call to action: guests sign up; members go back to "Name your world".
  const startBuilding = () => {
    if (!user) { navigate('/login?mode=signup'); return; }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.setTimeout(() => nameInput.current?.focus({ preventScroll: true }), 500);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <div className="landing-container" data-look={look}>
      {/* Hero background: looping ambient video with static fallback */}
      <div className="landing-hero-bg" />
      <video
        className="landing-hero-video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/hero-poster.webp"
        aria-hidden="true"
      >
        <source src="/hero-loop.webm" type="video/webm" />
        <source src="/hero-loop.mp4" type="video/mp4" />
      </video>
      <div className="landing-hero-veil" />
      {/* Atmospheric glow */}
      <div className="landing-glow" />

      <div className="landing-content">
        {/* Navigation */}
        <motion.nav
          className={`lx-nav ${scrolled ? 'lx-nav-solid' : ''}`}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <a className="lx-logo" href="/" aria-label="GathrXR home">
            <span className="gx-lockup nav-lockup">
              <img src="/brand/gathrxr-mark.png" alt="" />
              <span className="gx-word">Gathr<span>XR</span></span>
            </span>
          </a>
          <div className="lx-links">
            <a href="#feature-studio">Studio</a>
            <a href="#feature-avatars">Avatars</a>
            <a href="#feature-builder">Builder</a>
            <a href="#feature-games">Games</a>
            <a href="#import">Import</a>
            <a href="#npcs">NPCs</a>
            <a href="#devices">Devices</a>
          </div>
          <div className="lx-actions">
            <button className="lx-link-btn" onClick={() => navigate('/council')}><Crown size={15} /> <span>Council</span></button>
            {user ? (
              <>
                <button className="lx-link-btn" onClick={handleSignOut} title="Sign out" aria-label="Sign out"><LogOut size={15} /></button>
                <button className="lx-pill" onClick={() => navigate('/dashboard')}><LayoutDashboard size={15} /> Dashboard</button>
              </>
            ) : (
              <>
                <button className="lx-link-btn" onClick={() => navigate('/login')}>Sign in</button>
                <button className="lx-pill" onClick={() => navigate('/login?mode=signup')}>Start free</button>
              </>
            )}
          </div>
        </motion.nav>

        {/* Hero */}
        <section className="lx-hero">
          <motion.div
            className="lx-hero-inner"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <ClassBanner variant="chip" />
            {look === 'bold' && <span className="lx-chip"><span className="lx-chip-new">New</span> Studio + photo-to-3D avatars</span>}
            <h1 className="lx-title">
              {c.heroTitle[0]}<br />
              <span className="lx-title-grad">{c.heroTitle[1]}</span>
            </h1>
            <p className="lx-sub">{c.heroSub}</p>

            <div className="lx-cta">
              <div className="lx-create">
                <input
                  ref={nameInput}
                  type="text"
                  placeholder="Name your world…"
                  value={worldName}
                  onChange={(e) => setWorldName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createWorld()}
                  aria-label="Name your world"
                />
                <button onClick={createWorld}>Create world <ArrowRight size={18} /></button>
              </div>
              <a className="lx-ghost" href="#worlds">Explore worlds</a>
            </div>
            {!user && <p className="lx-hint">Free to join, with a free robot avatar and a free Avatar Builder. No download.</p>}

            <div className="lx-devices" aria-label="Works on">
              <span><Glasses size={16} /> Meta Quest</span>
              <span><Monitor size={16} /> Desktop</span>
              <span><Smartphone size={16} /> Phone & tablet</span>
              <span><Zap size={16} /> No install</span>
            </div>
          </motion.div>
          <motion.div
            className="lx-crew-wrap"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <HeroCrew />
          </motion.div>
          <a className="lx-scroll" href="#tour" aria-label="Scroll to features"><ChevronDown size={22} /></a>
        </section>

        {/* Product tour */}
        {/* Featured worlds (admin-curated) sit inside the tour, before the close */}
        <Showcase onStart={startBuilding} gallery={<WorldsGallery />} look={look} />
        <Ticker />

        {/* Footer */}
        <footer className="landing-footer">
          <span>&copy; {new Date().getFullYear()} GathrXR. All rights reserved.</span>
          <div className="footer-links">
            <a href="/docs">Docs</a>
            <a href="/council">Creator Council</a>
            <a href="/donate" title="Help us maintain a free plan during Beta (or perhaps longer)">☕ Support us</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="mailto:support@gathrxr.com">Contact</a>
          </div>
        </footer>
      </div>


    </div>
  );
};

export default LandingPage;
